import * as React from 'react';
import * as T from '@radix-ui/react-tabs';
import * as S from '@radix-ui/react-switch';
import * as TT from '@radix-ui/react-tooltip';
import * as DM from '@radix-ui/react-dropdown-menu';
import * as TG from '@radix-ui/react-toggle-group';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/* Tabs */
export const Tabs = T.Root;
export function TabsList({ className, ...p }: React.ComponentProps<typeof T.List>) {
  return <T.List className={cn('inline-flex h-10 items-center gap-1 rounded-md bg-paper-2 p-1', className)} {...p} />;
}
export function TabsTrigger({ className, ...p }: React.ComponentProps<typeof T.Trigger>) {
  return (
    <T.Trigger
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-[7px] px-3 text-[13px] font-medium text-muted transition data-[state=active]:bg-surface data-[state=active]:text-ink data-[state=active]:shadow-sm',
        className,
      )}
      {...p}
    />
  );
}
export const TabsContent = T.Content;

/* Switch */
export function Switch({ className, ...p }: React.ComponentProps<typeof S.Root>) {
  return (
    <S.Root
      className={cn(
        'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border border-border bg-paper-2 transition data-[state=checked]:border-primary data-[state=checked]:bg-primary',
        className,
      )}
      {...p}
    >
      <S.Thumb className="block size-5 translate-x-1 rounded-full bg-white shadow transition data-[state=checked]:translate-x-6" />
    </S.Root>
  );
}

/* Tooltip */
export const TooltipProvider = TT.Provider;
export function Tooltip({ content, children, side = 'top' }: { content: React.ReactNode; children: React.ReactNode; side?: 'top' | 'bottom' | 'left' | 'right' }) {
  return (
    <TT.Root delayDuration={400}>
      <TT.Trigger asChild>{children}</TT.Trigger>
      <TT.Portal>
        <TT.Content side={side} sideOffset={6} className="z-50 rounded-md bg-ink px-2 py-1 text-xs text-paper shadow">
          {content}
        </TT.Content>
      </TT.Portal>
    </TT.Root>
  );
}

/* Dropdown */
export const Menu = DM.Root;
export const MenuTrigger = DM.Trigger;
export function MenuContent({ className, ...p }: React.ComponentProps<typeof DM.Content>) {
  return (
    <DM.Portal>
      <DM.Content
        sideOffset={6}
        className={cn('z-50 min-w-48 rounded-md border border-border bg-surface p-1 shadow-lg', className)}
        {...p}
      />
    </DM.Portal>
  );
}
export function MenuItem({ className, ...p }: React.ComponentProps<typeof DM.Item>) {
  return (
    <DM.Item
      className={cn(
        'flex h-10 cursor-pointer select-none items-center gap-2 rounded-[7px] px-3 text-sm text-ink outline-none data-[highlighted]:bg-paper-2 data-[disabled]:opacity-50 [&_svg]:size-4',
        className,
      )}
      {...p}
    />
  );
}
export const MenuSeparator = () => <DM.Separator className="my-1 h-px bg-border" />;

/* Segmented control */
export function Segmented<T extends string>({
  value,
  onChange,
  items,
  className,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  items: { value: T; label: React.ReactNode; icon?: React.ReactNode }[];
  className?: string;
  label: string;
}) {
  return (
    <TG.Root
      type="single"
      value={value}
      onValueChange={(v) => v && onChange(v as T)}
      aria-label={label}
      className={cn('inline-flex rounded-md bg-paper-2 p-1', className)}
    >
      {items.map((i) => (
        <TG.Item
          key={i.value}
          value={i.value}
          className="inline-flex h-9 items-center gap-1.5 rounded-[7px] px-3 text-[13px] font-medium text-muted data-[state=on]:bg-surface data-[state=on]:text-ink data-[state=on]:shadow-sm [&_svg]:size-4"
        >
          {i.icon}
          {i.label}
        </TG.Item>
      ))}
    </TG.Root>
  );
}

/* Native select (best on iPad) */
export function Select({ className, children, ...p }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className={cn('relative', className)}>
      <select
        className="h-11 w-full appearance-none rounded-md border border-border bg-surface pl-3 pr-9 text-[14px] text-ink shadow-sm"
        {...p}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
    </div>
  );
}

/* Progress bar */
export function Progress({ value, className, tone = 'primary' }: { value: number; className?: string; tone?: 'primary' | 'ok' | 'warn' | 'danger' }) {
  const color = { primary: 'bg-primary', ok: 'bg-ok', warn: 'bg-warn', danger: 'bg-danger' }[tone];
  return (
    <div className={cn('h-2 w-full overflow-hidden rounded-full bg-paper-2', className)} role="progressbar" aria-valuenow={Math.round(value * 100)} aria-valuemin={0} aria-valuemax={100}>
      <div className={cn('h-full rounded-full transition-all', color)} style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%` }} />
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-paper-2', className)} />;
}

export function Spinner({ className }: { className?: string }) {
  return <span className={cn('inline-block size-5 animate-spin rounded-full border-2 border-primary border-t-transparent', className)} role="status" aria-label="loading" />;
}

export function Kbd({ children }: { children: React.ReactNode }) {
  return <kbd className="rounded border border-border bg-surface-2 px-1.5 py-0.5 font-mono text-[11px] text-muted">{children}</kbd>;
}
